/**
 * Legacy Files Page — 3-column layout (route /legacy-files).
 * Files List | PDF Viewer | Details Panel. Uses the backend API.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, AlertTriangle, Code01, Copy01, Eye, File06, FilterLines, LayoutLeft, RefreshCw05, Trash01 } from "@untitledui/icons";
import { ListBox, ListBoxItem, type ListBoxItemProps } from "react-aria-components";
import Markdown from "react-markdown";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Tab, TabList, TabPanel, Tabs } from "@/components/application/tabs/tabs";
import { Badge } from "@/components/base/badges/badges";
import { ButtonGroup, ButtonGroupItem } from "@/components/base/button-group/button-group";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icons";
import { getErrorDisplayMessage } from "@/lib/error-message-parser";
import { useDocumentsApi } from "@/services/api/documents-api";
import { useFilesApi } from "@/services/api/files-api";
import { FileStatusEnum, type FileStorageResponseDto, isFileErrorStatus, isFileFinalStatus, isFileProcessingStatus } from "@/types/api/files";
import { cx } from "@/utils/cx";

// =============================================================================
// CONSTANTS
// =============================================================================

const STATUS_FILTERS = [
    { id: "all", label: "All" },
    { id: FileStatusEnum.Pending, label: "Pending" },
    { id: FileStatusEnum.Uploaded, label: "Uploaded" },
    { id: FileStatusEnum.OCR, label: "OCR" },
    { id: FileStatusEnum.Classification, label: "Classification" },
    { id: FileStatusEnum.Processing, label: "Processing" },
    { id: FileStatusEnum.Completed, label: "Completed" },
    { id: FileStatusEnum.Failed, label: "Failed" },
] as const;

// =============================================================================
// HELPERS
// =============================================================================

type StatusFilter = "all" | FileStatusEnum;

/**
 * Get badge color for file status
 */
function getStatusColor(
    status: FileStatusEnum,
    errorCode?: string,
    hasRetryWarning?: boolean,
): "success" | "brand" | "gray-blue" | "error" | "warning" | "gray" {
    if (hasRetryWarning) return "warning";
    if (errorCode) return "error";
    switch (status) {
        case FileStatusEnum.Completed:
            return "success";
        case FileStatusEnum.Processing:
        case FileStatusEnum.OCR:
        case FileStatusEnum.Classification:
            return "brand";
        case FileStatusEnum.Pending:
        case FileStatusEnum.Uploaded:
            return "gray-blue";
        case FileStatusEnum.Failed:
            return "error";
        case FileStatusEnum.Deleted:
            return "gray";
        default:
            return "gray-blue";
    }
}

/**
 * Get display label for file status
 */
function getStatusLabel(status: FileStatusEnum, errorCode?: string, hasRetryWarning?: boolean): string {
    if (hasRetryWarning) return "Retrying";
    if (errorCode) return "Error";
    switch (status) {
        case FileStatusEnum.OCR:
            return "OCR";
        case FileStatusEnum.Classification:
            return "Classifying";
        default:
            return status;
    }
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

/**
 * Format date with time for details view
 */
function formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Truncate filename in the middle
 */
function truncateMiddle(text: string, maxLength: number = 30): string {
    if (text.length <= maxLength) return text;
    const start = Math.ceil((maxLength - 3) / 2);
    const end = Math.floor((maxLength - 3) / 2);
    return `${text.slice(0, start)}...${text.slice(-end)}`;
}

// =============================================================================
// FILE LIST ITEM
// =============================================================================

interface FileItemProps extends ListBoxItemProps<FileStorageResponseDto> {
    value: FileStorageResponseDto;
}

function FileItem({ value, className, ...props }: FileItemProps) {
    const isProcessing = isFileProcessingStatus(value.status) && !value.errorCode;
    const hasDocuments = value.documents && value.documents.length > 0;

    return (
        <ListBoxItem
            textValue={value.fileName}
            {...props}
            className={(state) =>
                cx(
                    "relative flex cursor-pointer flex-col gap-2 border-b border-secondary px-4 py-3 outline-none select-none",
                    state.isSelected && "border-l-2 border-l-blue-500 bg-gray-200",
                    typeof className === "function" ? className(state) : className,
                )
            }
        >
            {/* Line 1: File Name */}
            <div className="flex items-center justify-between gap-3">
                <span className="flex-1 truncate text-sm font-medium text-primary" title={value.fileName}>
                    {truncateMiddle(value.fileName, 30)}
                </span>
                <div className="flex flex-shrink-0 items-center gap-1">
                    {isProcessing && <div className="border-brand-primary h-3 w-3 animate-spin rounded-full border-2 border-t-transparent" />}
                </div>
            </div>

            {/* Line 2: Date • Page Count • Badge */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="text-xs text-tertiary">{formatDate(value.uploadedAt)}</span>
                    {value.pageCount > 0 && (
                        <>
                            <span className="text-xs text-quaternary">•</span>
                            <span className="text-xs text-tertiary">
                                {value.pageCount} {value.pageCount === 1 ? "page" : "pages"}
                            </span>
                        </>
                    )}
                    {hasDocuments && (
                        <>
                            <span className="text-xs text-quaternary">•</span>
                            <span className="text-xs text-tertiary">
                                {value.documents.length} {value.documents.length === 1 ? "doc" : "docs"}
                            </span>
                        </>
                    )}
                </div>

                <div className="flex flex-shrink-0 items-center gap-2">
                    <Badge size="sm" color={getStatusColor(value.status, value.errorCode, value.hasRetryWarning)} type="color">
                        {getStatusLabel(value.status, value.errorCode, value.hasRetryWarning)}
                    </Badge>
                </div>
            </div>
        </ListBoxItem>
    );
}

// =============================================================================
// FILE LIST PANEL
// =============================================================================

interface FileListPanelProps {
    files: FileStorageResponseDto[];
    filteredFiles: FileStorageResponseDto[];
    selectedFileId: number | null;
    onSelectionChange: (fileId: number) => void;
    filter: StatusFilter;
    onFilterChange: (filter: StatusFilter) => void;
    onRefresh: () => void;
    isRefreshing: boolean;
    onCollapse: () => void;
}

function FileListPanel({
    files,
    filteredFiles,
    selectedFileId,
    onSelectionChange,
    filter,
    onFilterChange,
    onRefresh,
    isRefreshing,
    onCollapse,
}: FileListPanelProps) {
    // Build filter options with counts
    const filterOptions = useMemo(() => {
        return STATUS_FILTERS.map((f) => ({
            ...f,
            count: f.id === "all" ? files.length : files.filter((file) => file.status === f.id).length,
        }));
    }, [files]);

    return (
        <div className="flex h-full w-96 max-w-sm flex-shrink-0 flex-col border-r border-secondary bg-primary">
            {/* Header */}
            <div className="flex-shrink-0 border-b border-secondary px-4 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-primary">Files</h2>
                        <Badge size="sm" color="gray" type="color">
                            {filteredFiles.length}
                        </Badge>
                    </div>

                    <div className="flex items-center gap-1">
                        {/* Refresh Button */}
                        <ButtonUtility
                            icon={RefreshCw05}
                            size="sm"
                            color="secondary"
                            tooltip="Refresh files"
                            onClick={onRefresh}
                            className={isRefreshing ? "animate-spin" : ""}
                        />

                        {/* Filter Dropdown */}
                        <Dropdown.Root>
                            <ButtonUtility icon={FilterLines} size="sm" color="secondary" tooltip="Filter files" />
                            <Dropdown.Popover>
                                <Dropdown.Menu
                                    selectedKeys={[filter]}
                                    onSelectionChange={(keys) => {
                                        const selectedKey = Array.from(keys).at(0) as StatusFilter;
                                        onFilterChange(selectedKey);
                                    }}
                                >
                                    {filterOptions.map((option) => (
                                        <Dropdown.Item
                                            key={option.id}
                                            id={option.id}
                                            label={option.label}
                                            addon={option.count > 0 ? option.count.toString() : undefined}
                                        />
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown.Popover>
                        </Dropdown.Root>

                        {/* Collapse Button */}
                        <ButtonUtility icon={LayoutLeft} size="sm" color="secondary" tooltip="Collapse panel" onClick={onCollapse} />
                    </div>
                </div>

                {/* Current Filter Display */}
                {filter !== "all" && (
                    <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs font-medium text-tertiary">{filterOptions.find((f) => f.id === filter)?.label}</span>
                    </div>
                )}
            </div>

            {/* File List */}
            <div className="flex-1 overflow-y-auto">
                <ListBox
                    aria-label="Files"
                    selectionMode="single"
                    items={filteredFiles}
                    selectedKeys={selectedFileId ? [selectedFileId.toString()] : []}
                    onSelectionChange={(keys) => {
                        const selectedId = Array.from(keys).at(0);
                        if (selectedId) {
                            onSelectionChange(parseInt(selectedId as string, 10));
                        }
                    }}
                >
                    {(item: FileStorageResponseDto) => <FileItem key={item.id} id={item.id.toString()} value={item} />}
                </ListBox>

                {/* Empty State */}
                {filteredFiles.length === 0 && (
                    <div className="flex flex-col items-center justify-center px-4 py-12">
                        <EmptyState size="sm">
                            <EmptyState.Header pattern="circle">
                                <EmptyState.FeaturedIcon icon={File06} color="gray" />
                            </EmptyState.Header>
                            <EmptyState.Content>
                                <EmptyState.Title>No files found</EmptyState.Title>
                                <EmptyState.Description>
                                    {filter !== "all" ? "No files match the current filter" : "Upload documents from the Home page to get started"}
                                </EmptyState.Description>
                            </EmptyState.Content>
                        </EmptyState>
                    </div>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// PDF VIEWER PANEL
// =============================================================================

interface PDFViewerPanelProps {
    file: FileStorageResponseDto | null;
    filesApi: ReturnType<typeof useFilesApi>;
    isListCollapsed?: boolean;
    onExpandList?: () => void;
}

function PDFViewerPanel({ file, filesApi, isListCollapsed, onExpandList }: PDFViewerPanelProps) {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Ref to track current URL for proper cleanup (avoids stale closure)
    const pdfUrlRef = useRef<string | null>(null);

    // Fetch PDF content when file changes
    useEffect(() => {
        // Cleanup previous URL using ref (not stale state)
        if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
            setPdfUrl(null);
        }

        if (!file?.id) {
            setIsLoading(false);
            setError(null);
            return;
        }

        const fetchPdfContent = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Call GetFileContent API
                const fileContent = await filesApi.getFileContent(file.id);

                // Convert base64 content to byte array
                const binaryString = atob(fileContent.content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                // Create blob from the bytes and generate URL
                const blob = new Blob([bytes], { type: fileContent.contentType });
                const url = URL.createObjectURL(blob);
                pdfUrlRef.current = url;
                setPdfUrl(url);
            } catch (err) {
                console.error("Failed to fetch file content:", err);
                setError(err instanceof Error ? err.message : "Failed to load file");
            } finally {
                setIsLoading(false);
            }
        };

        fetchPdfContent();

        // Cleanup on unmount or file change using ref
        return () => {
            if (pdfUrlRef.current) {
                URL.revokeObjectURL(pdfUrlRef.current);
                pdfUrlRef.current = null;
            }
        };
    }, [file?.id, filesApi]);

    // Expand bar shown when left panel is collapsed
    const expandBar = isListCollapsed ? (
        <div className="flex-shrink-0 border-b border-secondary bg-primary px-4 py-2">
            <ButtonUtility icon={LayoutLeft} size="sm" color="secondary" tooltip="Expand panel" onClick={onExpandList} />
        </div>
    ) : null;

    if (!file) {
        return (
            <div className="flex h-full flex-col bg-secondary">
                {expandBar}
                <div className="flex flex-1 items-center justify-center">
                    <EmptyState size="sm">
                        <EmptyState.Header pattern="circle">
                            <EmptyState.FeaturedIcon icon={File06} color="gray" />
                        </EmptyState.Header>
                        <EmptyState.Content>
                            <EmptyState.Title>No File Selected</EmptyState.Title>
                            <EmptyState.Description>Select a file from the left sidebar to view it here</EmptyState.Description>
                        </EmptyState.Content>
                    </EmptyState>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex h-full flex-col bg-secondary">
                {expandBar}
                <div className="flex flex-1 items-center justify-center">
                    <LoadingIndicator type="line-simple" size="md" label="Loading file..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-full flex-col bg-secondary">
                {expandBar}
                <div className="flex flex-1 items-center justify-center">
                    <EmptyState size="sm">
                        <EmptyState.Header pattern="circle">
                            <EmptyState.FeaturedIcon icon={AlertCircle} color="error" />
                        </EmptyState.Header>
                        <EmptyState.Content>
                            <EmptyState.Title>Failed to Load File</EmptyState.Title>
                            <EmptyState.Description>{error}</EmptyState.Description>
                        </EmptyState.Content>
                        <EmptyState.Footer>
                            <Button color="primary" onClick={() => window.location.reload()}>
                                Retry
                            </Button>
                        </EmptyState.Footer>
                    </EmptyState>
                </div>
            </div>
        );
    }

    if (!pdfUrl) {
        return (
            <div className="flex h-full flex-col bg-secondary">
                {expandBar}
                <div className="flex flex-1 items-center justify-center">
                    <EmptyState size="sm">
                        <EmptyState.Header pattern="circle">
                            <EmptyState.FeaturedIcon icon={File06} color="gray" />
                        </EmptyState.Header>
                        <EmptyState.Content>
                            <EmptyState.Title>No Preview Available</EmptyState.Title>
                            <EmptyState.Description>{file.fileName}</EmptyState.Description>
                        </EmptyState.Content>
                    </EmptyState>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col bg-secondary">
            {/* File Info Header */}
            <div className="flex-shrink-0 border-b border-secondary bg-primary px-4 py-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {isListCollapsed && <ButtonUtility icon={LayoutLeft} size="sm" color="secondary" tooltip="Expand panel" onClick={onExpandList} />}
                        <span className="max-w-md truncate text-sm font-medium text-secondary" title={file.fileName}>
                            {file.fileName}
                        </span>
                        {file.pageCount > 0 && (
                            <span className="text-xs text-tertiary">
                                ({file.pageCount} {file.pageCount === 1 ? "page" : "pages"})
                            </span>
                        )}
                    </div>
                    <Badge size="sm" color={getStatusColor(file.status, file.errorCode, file.hasRetryWarning)} type="color">
                        {getStatusLabel(file.status, file.errorCode, file.hasRetryWarning)}
                    </Badge>
                </div>
            </div>

            <div className="flex-1 overflow-hidden">
                <object data={pdfUrl} type="application/pdf" className="h-full w-full">
                    {/* Fallback if object tag fails */}
                    <div className="flex h-full flex-col items-center justify-center p-8">
                        <div className="mb-4 text-6xl">📁</div>
                        <h3 className="mb-2 text-lg font-medium text-primary">PDF Preview</h3>
                        <p className="mb-4 text-tertiary">{file.fileName}</p>
                        <div className="flex gap-2">
                            <Button color="primary" onClick={() => window.open(pdfUrl, "_blank")}>
                                Open PDF
                            </Button>
                        </div>
                    </div>
                </object>
            </div>
        </div>
    );
}

// =============================================================================
// FILE DETAILS PANEL
// =============================================================================

interface FileDetailsPanelProps {
    file: FileStorageResponseDto | null;
    onDelete?: (file: FileStorageResponseDto) => void;
    isDeleting?: boolean;
    rawText?: string | null;
    isRawTextLoading?: boolean;
}

function FileDetailsPanel({ file, onDelete, isDeleting = false, rawText = null, isRawTextLoading = false }: FileDetailsPanelProps) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("overview");
    const [rawViewMode, setRawViewMode] = useState<"preview" | "raw">("preview");
    const contentAreaRef = useRef<HTMLDivElement>(null);

    const tabs = [
        { id: "overview", label: "Overview" },
        { id: "documents", label: "Documents" },
        { id: "raw", label: "Raw" },
    ];

    // Handle document click - navigate to documents page
    const handleDocumentClick = (documentId: number) => {
        navigate(`/legacy-documents?id=${documentId}`);
    };

    const isProcessing = file ? isFileProcessingStatus(file.status) && !file.errorCode : false;
    const _isFinal = file ? isFileFinalStatus(file.status) : false;
    const isError = file ? isFileErrorStatus(file.status) || !!file.errorCode : false;
    const isRetryWarning = file ? !!file.hasRetryWarning : false;

    // Can delete files that are not currently processing
    const canDelete = file && !isProcessing;

    if (!file) {
        return (
            <div className="border-l border-secondary bg-primary" style={{ width: "384px", minWidth: "384px", maxWidth: "384px" }}>
                <div className="flex flex-col items-center justify-center px-6 py-12">
                    <EmptyState size="sm">
                        <EmptyState.Header pattern="circle">
                            <EmptyState.FeaturedIcon icon={File06} color="gray" />
                        </EmptyState.Header>
                        <EmptyState.Content>
                            <EmptyState.Title>No File Selected</EmptyState.Title>
                            <EmptyState.Description>Select a file to view details</EmptyState.Description>
                        </EmptyState.Content>
                    </EmptyState>
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex h-full flex-col overflow-hidden border-l border-secondary bg-primary"
            style={{ width: "384px", minWidth: "384px", maxWidth: "384px" }}
        >
            {/* Header */}
            <div className="flex-shrink-0 border-b border-secondary px-[18px] pt-[18px] pb-[18px]">
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-primary">Details</h2>
                    <div className="flex items-center gap-2">
                        <Badge size="sm" color={getStatusColor(file.status, file.errorCode, file.hasRetryWarning)} type="color">
                            {getStatusLabel(file.status, file.errorCode, file.hasRetryWarning)}
                        </Badge>
                        {isProcessing && <div className="border-brand-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />}
                    </div>
                </div>

                {/* Processing indicator */}
                {isProcessing && (
                    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-blue-700">
                        <div className="mb-2 flex items-center gap-2">
                            <div className="border-brand-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                            <span className="text-sm font-medium">Processing File</span>
                        </div>
                        <p className="text-xs">Your file is currently being processed.</p>
                    </div>
                )}

                {/* Retry warning indicator */}
                {isRetryWarning && (
                    <div className="border-warning mb-4 rounded-lg border bg-warning-25 p-3 text-warning-700">
                        <div className="mb-2 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-warning-primary" />
                            <span className="text-sm font-medium">Processing Paused</span>
                        </div>
                        <p className="text-xs">A transient error occurred. This job will be retried soon.</p>
                    </div>
                )}

                {/* Error indicator */}
                {isError && (
                    <div className="mb-4 rounded-lg border border-error bg-error-25 p-3 text-error-700">
                        <div className="mb-2 flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-error-primary" />
                            <span className="text-sm font-medium">Processing Failed</span>
                        </div>
                        <p className="text-xs">{file.errorCode ? getErrorDisplayMessage(file.errorCode) : "There was an error processing this file."}</p>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                    {canDelete && (
                        <Button
                            size="sm"
                            color="secondary-destructive"
                            iconLeading={Trash01}
                            onClick={() => onDelete?.(file)}
                            isDisabled={isDeleting}
                            isLoading={isDeleting}
                            className="flex-1"
                        >
                            Delete
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <Tabs
                key={`tabs-${file.id}`}
                defaultSelectedKey="overview"
                selectedKey={activeTab}
                onSelectionChange={(key) => setActiveTab(key as string)}
                className="flex flex-1 flex-col overflow-hidden"
            >
                <div className="flex-shrink-0 border-b border-secondary">
                    <TabList items={tabs} type="underline" size="sm" className="w-full justify-evenly px-[18px] pt-2">
                        {(item) => <Tab key={item.id} id={item.id} label={item.label} />}
                    </TabList>
                </div>

                {/* Content */}
                <div ref={contentAreaRef} className="min-h-0 w-full flex-1 overflow-y-auto" style={{ padding: "18px" }}>
                    <TabPanel id="overview" className="space-y-4">
                        <div>
                            <label className="mb-1 block text-xs font-medium text-tertiary">File ID</label>
                            <div className="rounded bg-secondary px-3 py-2 font-mono text-sm text-primary">{file.id}</div>
                        </div>

                        <div>
                            <label className="mb-1 block text-xs font-medium text-tertiary">File Name</label>
                            <div className="rounded bg-secondary px-3 py-2 text-sm break-all text-primary">{file.fileName}</div>
                        </div>

                        <div>
                            <label className="mb-1 block text-xs font-medium text-tertiary">Status</label>
                            <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">{getStatusLabel(file.status, file.errorCode)}</div>
                        </div>

                        <div>
                            <label className="mb-1 block text-xs font-medium text-tertiary">Uploaded</label>
                            <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">{formatDateTime(file.uploadedAt)}</div>
                        </div>

                        <div>
                            <label className="mb-1 block text-xs font-medium text-tertiary">File Size</label>
                            <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">{formatFileSize(file.fileSizeBytes)}</div>
                        </div>

                        <div>
                            <label className="mb-1 block text-xs font-medium text-tertiary">Page Count</label>
                            <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">
                                {file.pageCount} {file.pageCount === 1 ? "page" : "pages"}
                            </div>
                        </div>

                        <div>
                            <label className="mb-1 block text-xs font-medium text-tertiary">Content Type</label>
                            <div className="rounded bg-secondary px-3 py-2 text-sm text-primary">{file.contentType}</div>
                        </div>

                        {file.checksum && (
                            <div>
                                <label className="mb-1 block text-xs font-medium text-tertiary">Checksum</label>
                                <div className="rounded bg-secondary px-3 py-2 font-mono text-sm text-xs break-all text-primary">{file.checksum}</div>
                            </div>
                        )}
                    </TabPanel>

                    <TabPanel id="documents" className="space-y-4">
                        {file.documents && file.documents.filter((doc) => doc.isInbound === true).length > 0 ? (
                            <div className="space-y-3">
                                {file.documents
                                    .filter((doc) => doc.isInbound === true)
                                    .map((doc) => (
                                        <div
                                            key={doc.id}
                                            onClick={() => handleDocumentClick(doc.id)}
                                            className="cursor-pointer rounded-lg border border-tertiary bg-secondary p-3 transition-colors hover:border-secondary hover:bg-tertiary"
                                        >
                                            <div className="mb-2 flex items-center justify-between">
                                                <span className="truncate text-sm font-medium text-primary">{doc.generatedName || `Document #${doc.id}`}</span>
                                                <Badge size="sm" color="gray-blue" type="color">
                                                    {doc.status}
                                                </Badge>
                                            </div>
                                            {doc.documentType && <p className="text-xs text-tertiary">Type: {doc.documentType}</p>}
                                            <p className="text-xs text-quaternary">Created: {formatDate(doc.createdAt)}</p>
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <div className="py-8 text-center text-tertiary">
                                <p>No documents extracted from this file yet</p>
                            </div>
                        )}
                    </TabPanel>

                    <TabPanel id="raw" className="space-y-4">
                        <div className="mb-3 flex items-center justify-between">
                            <ButtonGroup
                                size="sm"
                                selectedKeys={[rawViewMode]}
                                onSelectionChange={(keys) => {
                                    const key = Array.from(keys).at(0) as "preview" | "raw" | undefined;
                                    if (key) setRawViewMode(key);
                                }}
                            >
                                <ButtonGroupItem id="preview" iconLeading={Eye}>
                                    Preview
                                </ButtonGroupItem>
                                <ButtonGroupItem id="raw" iconLeading={Code01}>
                                    Raw
                                </ButtonGroupItem>
                            </ButtonGroup>
                            {rawText && (
                                <ButtonUtility
                                    size="xs"
                                    color="secondary"
                                    icon={Copy01}
                                    tooltip="Copy raw text"
                                    onClick={() => {
                                        navigator.clipboard.writeText(rawText);
                                    }}
                                    className="ml-2 flex-shrink-0"
                                />
                            )}
                        </div>
                        {isRawTextLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <LoadingIndicator size="sm" />
                            </div>
                        ) : rawText ? (
                            rawViewMode === "preview" ? (
                                <div className="prose prose-sm max-w-none rounded bg-tertiary p-3 text-sm text-secondary">
                                    <Markdown>{rawText}</Markdown>
                                </div>
                            ) : (
                                <div className="rounded bg-tertiary p-3 font-mono text-xs text-tertiary">
                                    <pre className="break-words whitespace-pre-wrap">{rawText}</pre>
                                </div>
                            )
                        ) : (
                            <div className="py-8 text-center text-tertiary">
                                <p>No raw text available for this file</p>
                            </div>
                        )}
                    </TabPanel>
                </div>
            </Tabs>
        </div>
    );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function LegacyFilesPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filesApi = useFilesApi();
    const documentsApi = useDocumentsApi();

    // State
    const [files, setFiles] = useState<FileStorageResponseDto[]>([]);
    const [selectedFile, setSelectedFile] = useState<FileStorageResponseDto | null>(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<StatusFilter>("all");
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<FileStorageResponseDto | null>(null);
    const [isListCollapsed, setIsListCollapsed] = useState(false);
    const [rawText, setRawText] = useState<string | null>(null);
    const [isRawTextLoading, setIsRawTextLoading] = useState(false);

    // Get selected file ID from URL
    const selectedFileId = searchParams.get("id");
    const selectedFileIdNum = selectedFileId ? parseInt(selectedFileId, 10) : null;

    // Fetch files - initial load shows full loading state, refresh is silent
    const fetchFiles = useCallback(
        async (isInitial = false) => {
            try {
                if (isInitial) {
                    setIsInitialLoading(true);
                } else {
                    setIsRefreshing(true);
                }
                setError(null);

                const response = await filesApi.getFiles({
                    pageSize: 100,
                    skip: 0,
                });

                // Filter out deleted files
                const activeFiles = response.data.filter((file) => file.status !== FileStatusEnum.Deleted);

                // Sort by upload date (most recent first)
                const sortedFiles = activeFiles.sort((a, b) => {
                    return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime();
                });

                setFiles(sortedFiles);
            } catch (err) {
                console.error("Failed to fetch files:", err);
                setError(err instanceof Error ? err.message : "Failed to load files");
            } finally {
                setIsInitialLoading(false);
                setIsRefreshing(false);
            }
        },
        [filesApi],
    );

    // Initial fetch
    useEffect(() => {
        fetchFiles(true);
    }, [fetchFiles]);

    // Sync selection from URL and files
    useEffect(() => {
        if (selectedFileIdNum && !isNaN(selectedFileIdNum)) {
            const file = files.find((f) => f.id === selectedFileIdNum);
            setSelectedFile(file || null);
        } else {
            setSelectedFile(null);
        }
    }, [selectedFileIdNum, files]);

    // Fetch raw text when a file is selected
    useEffect(() => {
        if (!selectedFile) {
            setRawText(null);
            return;
        }

        let cancelled = false;
        setIsRawTextLoading(true);
        setRawText(null);

        filesApi
            .getFileRawText(selectedFile.id)
            .then((response) => {
                if (!cancelled) {
                    setRawText(response.rawText ?? null);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error("Failed to fetch raw text:", err);
                    setRawText(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsRawTextLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-fetch when file ID changes, not on every selectedFile reference change
    }, [selectedFile?.id, filesApi]);

    // Filter files
    const filteredFiles = useMemo(() => {
        if (filter === "all") {
            return files;
        }
        return files.filter((file) => file.status === filter);
    }, [files, filter]);

    // Track if we loaded with an explicit file ID to prevent auto-selection from overriding it
    const initialFileId = useRef(selectedFileId);

    // Auto-select first file when filter changes and current selection is not in filtered list
    // But preserve explicit file ID from URL
    useEffect(() => {
        if (filteredFiles.length > 0) {
            // If we have a file ID from URL that exists in filtered files, keep it
            if (selectedFileIdNum) {
                const fileExists = filteredFiles.some((file) => file.id === selectedFileIdNum);
                if (fileExists) {
                    return; // Keep the URL selection
                }
            }

            // Only auto-select if no file ID was in the initial URL
            if (!initialFileId.current && !selectedFileIdNum) {
                setSearchParams({ id: filteredFiles[0].id.toString() });
            }
        } else if (!selectedFileIdNum) {
            setSearchParams({});
            setSelectedFile(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredFiles]);

    // Handle file selection
    const handleSelectFile = (fileId: number) => {
        setSearchParams({ id: fileId.toString() });
    };

    // Handle refresh
    const handleRefresh = () => {
        fetchFiles(false);
    };

    // Handle delete - opens confirmation modal
    const handleDelete = (file: FileStorageResponseDto) => {
        setFileToDelete(file);
        setShowDeleteDialog(true);
    };

    // Handle confirmed delete - performs actual API call
    const handleConfirmDelete = async () => {
        if (!fileToDelete) return;

        try {
            setIsDeleting(true);
            await filesApi.deleteFile(fileToDelete.id);

            // Also delete all documents that belong to this file
            if (fileToDelete.documents && fileToDelete.documents.length > 0) {
                await Promise.all(fileToDelete.documents.map((doc) => documentsApi.deleteDocument(doc.id)));
            }

            // Refresh the file list
            await fetchFiles(false);

            // Clear selection if deleted file was selected
            if (fileToDelete.id === selectedFileIdNum) {
                setSearchParams({});
                setSelectedFile(null);
            }
        } catch (err) {
            console.error("Failed to delete file:", err);
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
            setFileToDelete(null);
        }
    };

    // Loading state (only for initial load, not background refresh)
    if (isInitialLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <LoadingIndicator type="line-simple" size="md" label="Loading files..." />
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <EmptyState size="sm">
                    <EmptyState.Header pattern="circle">
                        <EmptyState.FeaturedIcon icon={AlertCircle} color="error" />
                    </EmptyState.Header>
                    <EmptyState.Content>
                        <EmptyState.Title>Failed to Load Files</EmptyState.Title>
                        <EmptyState.Description>{error}</EmptyState.Description>
                    </EmptyState.Content>
                    <EmptyState.Footer>
                        <Button color="primary" onClick={() => fetchFiles(true)}>
                            Retry
                        </Button>
                    </EmptyState.Footer>
                </EmptyState>
            </div>
        );
    }

    // Empty state
    if (files.length === 0) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <EmptyState size="md">
                    <EmptyState.Header pattern="circle">
                        <EmptyState.Illustration type="documents" color="gray" />
                    </EmptyState.Header>
                    <EmptyState.Content>
                        <EmptyState.Title>No Files Found</EmptyState.Title>
                        <EmptyState.Description>Upload documents from the Home page to get started.</EmptyState.Description>
                    </EmptyState.Content>
                </EmptyState>
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 w-full overflow-hidden">
            {/* Left Column - File List */}
            {!isListCollapsed && (
                <div className="h-full flex-shrink-0">
                    <FileListPanel
                        files={files}
                        filteredFiles={filteredFiles}
                        selectedFileId={selectedFileIdNum}
                        onSelectionChange={handleSelectFile}
                        filter={filter}
                        onFilterChange={setFilter}
                        onRefresh={handleRefresh}
                        isRefreshing={isRefreshing}
                        onCollapse={() => setIsListCollapsed(true)}
                    />
                </div>
            )}

            {/* Center Column - PDF Viewer */}
            <div className="h-full min-w-0 flex-1 overflow-hidden">
                <PDFViewerPanel file={selectedFile} filesApi={filesApi} isListCollapsed={isListCollapsed} onExpandList={() => setIsListCollapsed(false)} />
            </div>

            {/* Right Column - File Details */}
            <div className="h-full flex-shrink-0">
                <FileDetailsPanel file={selectedFile} onDelete={handleDelete} isDeleting={isDeleting} rawText={rawText} isRawTextLoading={isRawTextLoading} />
            </div>

            {/* Delete File Confirmation Dialog */}
            {showDeleteDialog && fileToDelete && (
                <ModalOverlay isOpen isDismissable={false}>
                    <Modal className="max-w-md">
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-xl">
                                <div className="mb-4">
                                    <FeaturedIcon icon={Trash01} color="error" theme="light" size="md" />
                                </div>
                                <h2 className="mb-2 text-lg font-semibold text-primary">Delete File?</h2>
                                <p className="mb-6 text-tertiary">
                                    Are you sure you want to delete &ldquo;{fileToDelete.fileName}&rdquo;? This action cannot be undone.
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        color="secondary"
                                        size="md"
                                        onClick={() => {
                                            setShowDeleteDialog(false);
                                            setFileToDelete(null);
                                        }}
                                        isDisabled={isDeleting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button color="primary-destructive" size="md" iconLeading={Trash01} onClick={handleConfirmDelete} isLoading={isDeleting}>
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
}
